import {
	deviceName,
	inferredPlatform,
} from "@/components/devices/device-display";
import type {
	ConnectionConnectorGroup,
	ConnectionFacets,
	ConnectorDefinitionItem,
	DeviceWorkerItem,
} from "@/lib/api/connections";
import { buildConnectorApps } from "./app-catalog";
import { connectorIsDeviceBound } from "./facets";

/** File importers require local execution; os.files is not a device service. */
export function connectorIsDeviceCapability(
	connector: ConnectorDefinitionItem,
): boolean {
	return (
		connectorIsDeviceBound(connector) &&
		connector.required_capability !== "os.files"
	);
}

const RUNTIME_ONLY_FACETS: ConnectionFacets = {
	data: false,
	chat: false,
	actions: false,
	audience: false,
	inference: false,
	runtime: true,
};

/** Human label for the platform a device reports, for the row's meta line. */
function devicePlatformLabel(platform: string | null): string {
	if (platform === "macos") return "macOS";
	if (platform === "ios") return "iOS";
	if (platform === "chrome-extension") return "Chrome";
	// A bridge that polls without declaring a platform registers fine but can
	// advertise no capabilities — say so rather than inventing a name for it.
	return platform ?? "Unknown platform";
}

/** The same workspace predicate is used by the inventory, family and device pages. */
export function deviceInWorkspace(
	device: DeviceWorkerItem,
	ownerSlug: string,
): boolean {
	return (
		device.organization_slug === ownerSlug ||
		device.connectors.some((c) => c.organization_slug === ownerSlug)
	);
}

/** CLI and Docker are installation methods for the same headless family. */
export function deviceAppId(device: DeviceWorkerItem): string | undefined {
	const platform = inferredPlatform(device);
	return buildConnectorApps("", "https://example.invalid").find((app) =>
		app.devicePlatforms?.includes(platform ?? ""),
	)?.id;
}

interface DeviceConnectorRow {
	connector: ConnectorDefinitionItem;
	facets: ConnectionFacets;
	device: DeviceWorkerItem;
	devices: DeviceWorkerItem[];
	appId?: string;
}

/** One inventory row per runtime family, retaining each profile/device below it. */
export function buildDeviceConnectorRows(
	devices: readonly DeviceWorkerItem[],
	groups: readonly ConnectionConnectorGroup[],
	ownerSlug: string,
): DeviceConnectorRow[] {
	const groupsByKey = new Map(
		groups.map((group) => [group.connector_key, group]),
	);
	const rows = devices
		.filter((device) => deviceInWorkspace(device, ownerSlug))
		.map((device) => {
			// The device replaces these rows, so Data / Actions filters must still
			// expose the connections it now contains, scoped to this workspace.
			const facets = { ...RUNTIME_ONLY_FACETS };
			for (const connection of device.connectors) {
				if (connection.organization_slug !== ownerSlug) continue;
				const group = groupsByKey.get(connection.connector_key);
				if (
					!group?.connections.some(
						(item) => item.id === connection.connection_id,
					)
				)
					continue;
				for (const key of Object.keys(facets) as (keyof ConnectionFacets)[]) {
					facets[key] ||= group.facets[key];
				}
			}
			return {
				device,
				devices: [device],
				appId: deviceAppId(device),
				facets,
				connector: {
					// List identity only — a device routes by its own segment.
					key: `device-row:${device.id}`,
					name: deviceName(device),
					description: null,
					version: "",
					auth_schema: null,
					feeds_schema: null,
					actions_schema: null,
					options_schema: null,
					status: device.online ? "active" : "inactive",
					login_enabled: false,
					installed: true,
				} as ConnectorDefinitionItem,
			};
		});
	const families = new Map<string, DeviceConnectorRow>();
	for (const row of rows) {
		const key = row.appId ?? row.connector.key;
		const existing = families.get(key);
		if (existing) {
			existing.devices.push(row.device);
			for (const facet of Object.keys(row.facets) as (keyof ConnectionFacets)[])
				existing.facets[facet] ||= row.facets[facet];
		} else {
			const app = buildConnectorApps(ownerSlug, "https://example.invalid").find(
				(app) => app.id === row.appId,
			);
			families.set(key, {
				...row,
				connector: {
					...row.connector,
					key,
					name: app?.name ?? row.connector.name,
				},
			});
		}
	}
	return [...families.values()];
}

/** The row's secondary line: platform, what it serves, and whether it's awake. */
export function deviceRowMeta(
	device: DeviceWorkerItem,
	ownerSlug?: string,
): string {
	const platform = devicePlatformLabel(inferredPlatform(device));
	const count = ownerSlug
		? device.connectors.filter((c) => c.organization_slug === ownerSlug).length
		: device.connector_count;
	const connectors = `${count} ${count === 1 ? "connector" : "connectors"}`;
	return [platform, connectors, device.online ? "online" : "offline"].join(
		" · ",
	);
}
